import { mdiChevronRight, mdiMinus, mdiPlus, mdiSchool } from '@mdi/js';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
    ListItemFooter,
} from 'common/components/ListItem';
import {
    IExternalContentListItemFragment,
    IPortfolioItemProgressFragment,
} from 'graphql/types';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { ChipGroup } from 'common/components/Chip';

interface IProps extends React.ComponentProps<typeof ListItem> {
    addButton?: boolean;
    added?: boolean;
    disabled?: boolean;
    portfolioItem?: IPortfolioItemProgressFragment | null;
    selected?: boolean;
    externalContent?: IExternalContentListItemFragment;
    footer?: React.ReactNode;
    hideCategoryChips?: boolean;
    onClick?(externalContent: IExternalContentListItemFragment): void;
}

export const ExternalContentListItem = ({
    addButton,
    added,
    disabled,
    portfolioItem,
    selected,
    externalContent,
    footer,
    onClick,
    hideCategoryChips,
    loading,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { id, imageUrl, title, extraCategoryValues } = externalContent || {};

    let mediaIconPath = mdiSchool;

    if (addButton) {
        mediaIconPath = selected || added ? mdiMinus : mdiPlus;
    }

    const hasIcon = (disabled && !imageUrl) || !disabled;

    const showActionText =
        !addButton &&
        !disabled &&
        !!externalContent &&
        ((loading && portfolioItem) || (!loading && !portfolioItem));
    let actionText = (
        <>
            <ListItemActionText>{translate('open')}</ListItemActionText>
            <ListItemSecondaryAction>
                <Icon path={mdiChevronRight} size="3rem" />
            </ListItemSecondaryAction>
        </>
    );

    if (loading) {
        // Show progress icon when loading
        actionText = (
            <ListItemSecondaryAction>
                <CircularProgress color="primary" size={30} />
            </ListItemSecondaryAction>
        );
    }

    return (
        <ListItem
            button={!disabled && !!externalContent}
            disableHover={disabled || !externalContent}
            key={id}
            px={0}
            selected={selected}
            sx={{
                flexWrap: !!footer ? 'wrap' : undefined,
            }}
            onClick={() =>
                !disabled && !!externalContent && onClick?.(externalContent)
            }
            {...other}
        >
            <ListItemMedia color="primary" image={imageUrl} size="small">
                {hasIcon && (
                    <Icon path={mediaIconPath} size="2rem" sizeSm="3rem" />
                )}
            </ListItemMedia>

            <ListItemText
                primary={
                    !!externalContent ? title : translate('moduleNotAvailable')
                }
            >
                {!!externalContent &&
                    !!extraCategoryValues &&
                    !hideCategoryChips && (
                        <ChipGroup>
                            <ExtraCategoryLabels
                                extraCategoryValues={extraCategoryValues}
                            />
                        </ChipGroup>
                    )}
            </ListItemText>

            {showActionText && actionText}

            {!loading && !!portfolioItem && (
                <PortfolioItemProgressScoreChip portfolioItem={portfolioItem} />
            )}

            {footer && !!externalContent && (
                <ListItemFooter>{footer}</ListItemFooter>
            )}
        </ListItem>
    );
};
